import React, { useState } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import QueryRow from './QueryRow';
import { useSelector } from 'react-redux';
import { initialTileState, Query } from '../../../types/tileBuilderTypes';
import { RootState, useAppDispatch } from '../../../support/store';
import {
  addQuery,
  removeQuery,
  updateQuery,
  setQueryResult,
} from '../../../support/slices/tileBuilderSlice';
import { executeQueriesWithChaining } from '../../../support/queryChainExecutor';
import { useTileSourceCatalog } from '../../../hooks/toolbox-hooks';

const QueryBuilder: React.FC<{ chartId: string }> = ({ chartId }) => {
  const EMPTY_ARRAY: any[] = [];
  const tileBuilder = useSelector(
    (state: RootState) => state.tileBuilder[chartId]?.tile || initialTileState
  );
  const queries = tileBuilder.queries ?? EMPTY_ARRAY;
  const dispatch = useAppDispatch();

  const userData: any = useSelector((state: RootState) => state.user.userData);
  const previewRuntimeContext: any = useSelector(
    (state: RootState) => state.tileBuilder[chartId]?.preview?.runtimeContext || {}
  );

  const availableVariables: Record<string, string> = {
    name: userData?.name ?? '',
    email: userData?.email ?? '',
    sfId: userData?.sfId ?? '',
    current_time_filter: previewRuntimeContext?.current_time_filter ?? 'all',
  };

  const {
    isLoading: sourceCatalogLoading,
    error: sourceCatalogError,
  } = useTileSourceCatalog();

  const [isExecuting, setIsExecuting] = useState(false);
  const [executionError, setExecutionError] = useState<string | null>(null);

  const handleAddQuery = () => {
    const newQuery: Query = {
      dataSource: '',
      model: '',
      entity: '',
      function: '',
      path: '',
      method: 'GET',
      params: {},
      payload: '',
      queryResult: undefined,
      functionResult: undefined,
      dependsOn: [],
      transformFunction: '',
      useResultFrom: undefined,
      resultMapping: {},
      localFilePath: '',
      localFileName: '',
      enableDateFilter: false,
    };
    dispatch(addQuery({ chartId, query: newQuery }));
  };

  const handleUpdateQuery = (index: number, updatedQuery: Query) => {
    const normalized: Query = {
      dataSource: updatedQuery.dataSource ?? '',
      model: updatedQuery.model ?? '',
      entity: updatedQuery.entity ?? '',
      function: updatedQuery.function ?? '',
      path: updatedQuery.path ?? '',
      method: updatedQuery.method ?? 'GET',
      params: updatedQuery.params ?? {},
      payload: updatedQuery.payload ?? '',
      queryResult: updatedQuery.queryResult,
      functionResult: updatedQuery.functionResult,
      dependsOn: updatedQuery.dependsOn ?? [],
      transformFunction: updatedQuery.transformFunction ?? '',
      useResultFrom: updatedQuery.useResultFrom,
      resultMapping: updatedQuery.resultMapping ?? {},
      localFilePath: updatedQuery.localFilePath ?? '',
      localFileName: updatedQuery.localFileName ?? '',
      enableDateFilter: updatedQuery.enableDateFilter ?? false,
      runtimeParams: updatedQuery.runtimeParams ?? {},
    };
    dispatch(updateQuery({ chartId, index, query: normalized }));
  };

  const handleDeleteQuery = (index: number) => {
    dispatch(removeQuery({ chartId, index }));
  };

  const handleExecuteAllQueries = async () => {
    setIsExecuting(true);
    setExecutionError(null);

    try {
      const results = await executeQueriesWithChaining({
        queries,
        availableVariables,
      });

      results.forEach((result) => {
        if (!result.error) {
          dispatch(
            setQueryResult({
              chartId,
              index: result.queryIndex,
              result: result.transformedResult || result.rawResult,
            })
          );
        }
      });

      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        setExecutionError(
          `${errors.length} quer${errors.length === 1 ? 'y' : 'ies'} failed. Check console for details.`
        );
      }
    } catch (error: any) {
      console.error('Query execution error:', error);
      setExecutionError(error.message || 'Failed to execute queries');
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Build Queries</Typography>
        <Button
          onClick={handleExecuteAllQueries}
          variant="outlined"
          color="primary"
          disabled={queries.length === 0 || isExecuting || sourceCatalogLoading}
          startIcon={isExecuting ? <CircularProgress size={16} /> : null}
        >
          {isExecuting ? 'Executing...' : 'Execute All Queries'}
        </Button>
      </Box>

      {sourceCatalogLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading tile source configuration...
        </Alert>
      )}

      {sourceCatalogError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load tile source configuration.
        </Alert>
      )}

      {executionError && (
        <Alert severity="error" onClose={() => setExecutionError(null)} sx={{ mb: 2 }}>
          {executionError}
        </Alert>
      )}

      {!sourceCatalogLoading &&
        queries.map((query, index) => (
          <Box key={index} mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Query {index + 1}
              {query.dependsOn && query.dependsOn.length > 0 && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ ml: 2, color: 'text.secondary' }}
                >
                  (Depends on: {query.dependsOn.map((i) => `Query ${i + 1}`).join(', ')})
                </Typography>
              )}
            </Typography>
            <QueryRow
              chartId={chartId}
              query={query}
              onUpdate={(updatedQuery) => handleUpdateQuery(index, updatedQuery)}
              onDelete={() => handleDeleteQuery(index)}
              allQueries={queries}
            />
          </Box>
        ))}

      <Button
        onClick={handleAddQuery}
        variant="contained"
        sx={{ mt: 2 }}
        disabled={sourceCatalogLoading}
      >
        Add Query
      </Button>
    </Box>
  );
};

export default QueryBuilder;